import { respondToSupplyLog } from '@backend/functions/gyn'
import { Alert, AlertIcon, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { useApp, usePopUpMessage } from '@hb/react'
import { Encounter, SupplyLogEntry } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { CdiEntryPrompt } from '../../EncounterView/CdiPromptBanner'

// §6.7: Shown inside the auth modal for birthing center encounters when the
// supply log hasn't been reviewed. Provider must choose one option before
// AuthenticateAndSubmit is shown — cannot dismiss without selecting.
// When pending CDI items exist they are shown inline so the provider can
// confirm/dismiss without closing the modal. The gate auto-clears once all
// detected items have been responded to. When no CDI was detected the
// provider must explicitly confirm no billable supplies.
export const SupplyLogPreAuthGate = ({
  encounter,
  examId,
}: {
  encounter: Encounter
  examId: string
}) => {
  const [saving, setSaving] = useState(false)
  const { appName } = useApp()
  const { showError } = usePopUpMessage()
  const supplyLog = useMemo(() => encounter.supplyLog ?? [], [encounter.supplyLog])

  const pendingItems = useMemo(
    () => supplyLog.filter((e: SupplyLogEntry) => !e.confirmedByProvider && !e.dismissedByProvider),
    [supplyLog],
  )

  const confirmNoBillable = useCallback(async () => {
    setSaving(true)
    try {
      // Firestore rules allow no client writes to ENCOUNTERS — persist via the
      // provider-permitted callable ('mark-reviewed' sets supplyLogReviewed).
      await respondToSupplyLog({ encounterId: examId, appName, action: 'mark-reviewed' })
    } catch (err) {
      showError((err as Error)?.message ?? 'Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [examId, appName, showError])

  // Auto-clear: all detected items have been responded to — no further action needed.
  const allResponded = supplyLog.length > 0 && pendingItems.length === 0

  if (encounter.supplyLogReviewed || allResponded) return null

  return (
    <Alert
      status="warning"
      borderRadius={4}
      mx={4}
      mb={2}
      py={2}
      flexDirection="column"
      alignItems="flex-start"
      fontSize="sm">
      <HStack mb={1}>
        <AlertIcon />
        <Text fontWeight={600} fontSize="sm">
          Supply log review required
        </Text>
      </HStack>
      {pendingItems.length > 0 ? (
        <VStack align="stretch" spacing={1} w="100%" pl={6} mb={1}>
          <Text fontSize="xs" mb={1}>
            {pendingItems.length} potential supply item{pendingItems.length !== 1 ? 's' : ''}{' '}
            detected. Confirm or dismiss each item before authenticating.
          </Text>
          {pendingItems.map(entry => (
            <CdiEntryPrompt key={entry.id} entry={entry} encounterId={examId} />
          ))}
        </VStack>
      ) : (
        <>
          <Text fontSize="xs" mb={2} pl={6}>
            Please confirm whether any billable supplies were used before authenticating.
          </Text>
          <HStack pl={6} spacing={2}>
            <Button size="xs" colorScheme="orange" isLoading={saving} onClick={confirmNoBillable}>
              No billable supplies this visit
            </Button>
          </HStack>
        </>
      )}
    </Alert>
  )
}
